



















































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import {
  BeschreibungFile,
  BeschreibungRow,
  cellValue,
  LABEL_COLUMNS,
  ReferencedValues,
  referenceNumberAt,
  referencesTo,
  RowReference,
  ShiftedReference,
  shiftedReferences,
} from "@/utils/BeschreibungFile";

/** A cell to set: its position in the line and its CSV value. */
export interface BeschreibungCellChange {
  cell: number;
  value: string;
}

/**
 * Edits one row of a draft: its labels, its type and its values, each in its CSV value (a leading `"`
 * there is IRES Project's "hide in description" marker and stays as typed). A `/` value splits the row
 * without taking a number; blank values take none either, which the number column shows. Values are
 * written back cell by cell, so a cell left alone stays byte for byte. Moving or removing a value
 * renumbers the ones after it; references elsewhere are not rewritten, so the dialog lists those that
 * would name another value.
 *
 * `show()` resolves with the cells to set, or null.
 */
@Component({ components: { "base-dialog": BaseDialogComponent, "confirmation-dialog": ConfirmationDialogComponent } })
export default class BeschreibungRowDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  labels: string[] = [];
  fieldType = "";
  values: string[] = [];
  typeCell = 5;
  rowKey = "";
  /** The references to this row's values elsewhere in the file, taken when the dialog opens. */
  references: RowReference[] = [];
  initialValues: ReferencedValues = { fieldType: "", values: [] };
  private initial = "";
  private initialValueCount = 0;
  private resolve: ((changes: BeschreibungCellChange[] | null) => void) | null = null;

  readonly labelNames = LABEL_COLUMNS;
  readonly typeItems = ["", "E", "M", "T", "A"].map((value) => ({ value, text: value === "" ? "–" : value }));

  show(file: BeschreibungFile, row: BeschreibungRow): Promise<BeschreibungCellChange[] | null> {
    this.typeCell = row.typeCell;
    this.labels = row.labels.map((_, index) => cellValue(file, row.lineIndex, 1 + index));
    this.fieldType = cellValue(file, row.lineIndex, row.typeCell);
    this.values = row.values.map((_, index) => cellValue(file, row.lineIndex, row.typeCell + 1 + index));
    this.initialValueCount = this.values.length;
    this.rowKey = row.key;
    this.references = referencesTo(file, row.key);
    this.initialValues = { fieldType: this.fieldType, values: [...this.values] };
    this.initial = this.snapshot;
    // Persistent and closeOnSave false: edit mode is never clicked away, and ÜBERNEHMEN closes itself.
    this.baseDialog.show({
      title: this.$t("admin.infoConfig.draft.rowDialogTitle", [row.key]).toString(),
      positiveButtonText: this.$t("common.apply").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 720,
      scrollable: true,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  get snapshot(): string {
    return JSON.stringify([this.labels, this.fieldType, this.values]);
  }

  get hasChanged(): boolean {
    return this.snapshot !== this.initial;
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  /** The number references use for the value at `position` (see `referenceNumberAt`), or "–" for none. */
  optionNumberOf(position: number): string {
    return String(referenceNumberAt({ fieldType: this.fieldType, values: this.values }, position) ?? "–");
  }

  /** The references that would name another value once applied (moving or removing renumbers the rest). */
  get shifted(): ShiftedReference[] {
    return shiftedReferences(this.references, this.initialValues, { fieldType: this.fieldType, values: this.values });
  }

  /** "Boden0010-3: „betroffene Fläche“ → „Raumumfang“, verwendet in Boden0555, Boden0070 und 9 weiteren". */
  describeShift(item: ShiftedReference): string {
    const shown = item.heldBy.slice(0, 3).join(", ");
    const heldBy =
      item.heldBy.length > 3 ? this.$t("admin.infoConfig.draft.referencedByMore", [shown, item.heldBy.length - 3]).toString() : shown;
    const reference = `${this.rowKey}-${item.number}`;
    return item.after === null
      ? this.$t("admin.infoConfig.draft.referenceGone", [reference, item.before, heldBy]).toString()
      : this.$t("admin.infoConfig.draft.referenceShift", [reference, item.before, item.after, heldBy]).toString();
  }

  move(index: number, delta: number) {
    const target = index + delta;
    const values = [...this.values];
    [values[index], values[target]] = [values[target], values[index]];
    this.values = values;
  }

  /** Every cell whose value changed; a removed value leaves its cell blank (the line keeps its width). */
  onApply() {
    const changes: BeschreibungCellChange[] = [];
    this.labels.forEach((value, index) => changes.push({ cell: 1 + index, value }));
    changes.push({ cell: this.typeCell, value: this.fieldType });
    const count = Math.max(this.values.length, this.initialValueCount);
    for (let index = 0; index < count; index++) {
      changes.push({ cell: this.typeCell + 1 + index, value: this.values[index] ?? "" });
    }
    this.close(changes);
  }

  close(changes: BeschreibungCellChange[] | null) {
    this.baseDialog.isShowing = false;
    this.finish(changes);
  }

  private finish(changes: BeschreibungCellChange[] | null) {
    this.resolve?.(changes);
    this.resolve = null;
  }
}
